import React from 'react';
import { Button } from 'primereact/button';
import { ToastService } from '@iamsoftware/react-hooks';
import { useDataTable } from 'src/hooks/useAppDataTable';
import { useNavigate } from 'react-router-dom';
import { AgreementService } from '../../../../../service/AgreementService';
import { DocumentNameCell } from '../DocumentNameCell';

interface DraftTabProps {
    handleView: (rowData: any) => void;
    handleSign: (item: any) => void;
    handleSignFromDraft: (rowData: any) => void;
    handleSendEmail: (item: any) => void;
    handleEdit: (rowData: any) => void;
    handleDelete: (rowData: any) => void;
    setDisplayUploadAttachmentDialog: (data: { agreementId: string, agreementName: string } | null) => void;
    sendingEmail: boolean;
    setReload?: (reloadFn: () => void) => void;
    handleBulkSign?: (items?: any[]) => void;
    headerActions?: React.ReactNode;
}

export const DraftTab: React.FC<DraftTabProps> = ({
    handleView,
    handleSign,
    handleSignFromDraft,
    handleSendEmail,
    handleEdit,
    handleDelete,
    setDisplayUploadAttachmentDialog,
    sendingEmail,
    setReload,
    handleBulkSign,
    headerActions
}) => {
    const navigate = useNavigate();
    const [isBulkMode, setIsBulkMode] = React.useState(false);
    const draftColumns: any[] = [
        ...(isBulkMode ? [{ selectionMode: 'multiple', width: 50 }] : []),
        {
            field: 'agreementName',
            header: 'Tên tài liệu',
            width: 100,
            matchMode: 'contains',
            dataType: 'custom' as const,
            customCell(rowData: any): any {
                return (
                    <DocumentNameCell
                        rowData={rowData}
                        onClick={() => handleView(rowData)}
                        title="Click để xem chi tiết"
                    />
                );
            }
        },
        { field: 'agreementNo', header: 'Mã tài liệu', width: 120, matchMode: 'contains' },
        { field: 'agreementTypeName', header: 'Loại tài liệu', width: 150, matchMode: 'contains' },
        // {
        //     field: 'statusDescription',
        //     header: 'Trạng thái',
        //     width: 120,
        //     dataType: 'custom' as const,
        //     customCell(rowData: any): any {
        //         const isUrgent = rowData.isUrgent === 'Y';
        //         const isImportant = rowData.isImportant === 'Y';
        //         return (
        //             <div className="flex align-items-center gap-2 flex-wrap">
        //                 <Tag value="Nháp" severity="secondary" />
        //                 {isUrgent && (
        //                     <span className="flex align-items-center gap-1 text-orange-500 text-sm" title="Tài liệu cần xử lý gấp">
        //                         <i className="pi pi-bolt"></i>
        //                         <span>Khẩn cấp</span>
        //                     </span>
        //                 )}
        //                 {isImportant && (
        //                     <span className="flex align-items-center gap-1 text-indigo-500 text-sm" title="Tài liệu quan trọng">
        //                         <i className="pi pi-bookmark"></i>
        //                         <span>Quan trọng</span>
        //                     </span>
        //                 )}
        //             </div>
        //         );
        //     }
        // },
        // {
        //     field: 'mainContent',
        //     header: 'File ký',
        //     width: 200,
        //     dataType: 'custom' as const,
        //     customCell(rowData: any): any {
        //         if (!rowData.mainFileName && !rowData.mainContentLocation) return <span className="text-gray-500">Chưa có file</span>;
        //         const isPdf = rowData.mainContentType === 'application/pdf';
        //         const isWord = rowData.mainContentType?.includes('word') || rowData.mainContentType?.includes('document');
        //         const iconClass = isPdf ? 'pi-file-pdf' : (isWord ? 'pi-file-word' : 'pi-file');
        //         return (
        //             <div className="flex align-items-center gap-2">
        //                 <i className={`pi ${iconClass} text-xl`}></i>
        //                 <div>
        //                     <div className="font-medium">{rowData.mainFileName}</div>
        //                 </div>
        //             </div>
        //         );
        //     }
        // },
        {
            field: 'expiryDate',
            header: 'Thời gian',
            width: 180,
            dataType: 'custom',
            customCell(rowData: any): any {
                const formatLocal = (value?: any) => {
                    if (!value) return null;
                    const d = new Date(value);
                    const dd = String(d.getDate()).padStart(2, '0');
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const yyyy = d.getFullYear();
                    const HH = String(d.getHours()).padStart(2, '0');
                    const MM = String(d.getMinutes()).padStart(2, '0');
                    const SS = String(d.getSeconds()).padStart(2, '0');
                    return `${dd}/${mm}/${yyyy} ${HH}:${MM}:${SS}`;
                };

                const expiryText = rowData.expiryDate ? formatLocal(rowData.expiryDate) : null;
                const updatedText = rowData.lastUpdatedStamp ? formatLocal(rowData.lastUpdatedStamp) : null;

                if (!expiryText && !updatedText) return null;

                return (
                    <div className="text-sm">
                        {expiryText && <div className={updatedText ? 'mb-1' : undefined}>{expiryText}</div>}
                        {updatedText && (
                            <div>{updatedText}</div>
                        )}
                    </div>
                );
            }
        },
    ];

    const { render: renderDraft, reloadLazyData: reloadDraft, selectedItems } = useDataTable({
        recordLabel: 'tài liệu nháp',
        headerActions: (
            <>
                {headerActions}
                    {!isBulkMode ? (
                        <Button 
                            label="Ký hàng loạt" 
                            icon="pi pi-user-edit" 
                            className="p-button-success p-button-sm" 
                            onClick={() => setIsBulkMode(true)}
                        />
                    ) : (
                        <>
                            <Button 
                                label="Xác nhận" 
                                icon="pi pi-check" 
                                className="p-button-success p-button-sm" 
                                onClick={() => {
                                    handleBulkSign?.(selectedItems);
                                    setIsBulkMode(false);
                                }}
                            />
                            <Button 
                                label="Bỏ qua" 
                                icon="pi pi-times" 
                                className="p-button-secondary p-button-sm" 
                                onClick={() => setIsBulkMode(false)}
                            />
                        </>
                    )}
            </>
        ),
        selectionMode: isBulkMode ? 'multiple' : undefined,
        dataKey: 'agreementId',
        indexColumnWidth: 56,
        actionColumnWidth: 190,
        columns: draftColumns,
        getList: lazyLoadEvent => {
            return AgreementService.getAgreementList(lazyLoadEvent, 'AGT_DRAFT').then(result => {
                if (result.listData && Array.isArray(result.listData)) {
                    result.listData = result.listData.map((item: any) => ({
                        ...item,
                        mainContent: item.mainFileName || item.mainContentType ? {
                            fileName: item.mainFileName,
                            contentType: item.mainContentType,
                            contentLocation: item.referenceContentLocation || item.mainContentLocation,
                            fileId: item.fileId || item.mainFileId || item.mainContentId,
                            googleFileId: item.fileId || item.mainFileId || item.mainContentId,
                            referenceFileId: item.referenceContentLocation
                        } : null
                    }));
                }
                return {
                    listData: result.listData || [],
                    totalRecords: result.totalRecords || (result.listData || []).length
                };
            });
        },
        initActions(items: Array<any>) {
            if (items) {
                items.forEach(item => {
                    item.actions = [
                        { icon: 'pi pi-eye', label: 'Xem chi tiết', command: () => handleView(item) },
                        { icon: 'pi pi-pen-to-square', title: 'Ký tài liệu', className: 'p-button-info', command: () => handleSign(item) },
                        //{ icon: 'pi pi-user-edit', title: 'Bắt đầu ký', className: 'p-button-success', command: () => handleSignFromDraft(item) },
                        { icon: 'pi pi-send', title: 'Gửi email', className: 'p-button-help', command: () => { if (!sendingEmail) handleSendEmail(item); } },
                        {
                            icon: 'pi pi-paperclip',
                            title: 'File đính kèm',
                            className: 'p-button-info',
                            props: {
                                badge: Number(item.attachmentCount) > 0 ? String(item.attachmentCount) : undefined,
                                badgeClassName: 'p-badge-info'
                            },
                            command: () => setDisplayUploadAttachmentDialog({ agreementId: item.agreementId, agreementName: item.agreementName })
                        }
                    ];

                    item.actionMenus = [
                        
                        { icon: 'pi pi-pencil', label: 'Chỉnh sửa', command: () => handleEdit(item) },
                        { icon: 'pi pi-trash', label: 'Xóa', command: () => handleDelete(item) }
                    ];
                });
            }
        },
        originalProps: { scrollHeight: 'calc(100vh - 300px)' }
    });

    React.useEffect(() => {
        if (setReload) setReload(reloadDraft);
    }, [reloadDraft, setReload]);

    return (
        <div className="tai-lieu-ky-table">
            {renderDraft()}
        </div>
    );
};
